import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Container,
  Content,
  SearchBar,
  PlayerGrid,
  PlayerCard,
  PlayerImage,
  PlayerInfo,
  TeamInfo,
  StatsInfo,
  Skeleton,
  PlayerCardSkeleton,
  SkeletonGrid,
} from "./styles";
import { FaSearch } from "react-icons/fa";
import { useSearchPlayers, Player } from "@/hooks/fetchHooks/playersFetch";
import { translatePosition } from "@/utils/translatePositions";

// Componente para o skeleton card
const PlayerSkeleton = () => (
  <PlayerCardSkeleton>
    {/* Imagem do jogador */}
    <Skeleton variant="rectangular" width="85%" height={120} 
      style={{ margin: "0 auto 20px", backgroundColor: "#ffffff5e", borderRadius: "3px" }} />
    
    {/* Posição e nome */}
    <Skeleton variant="text" width="40%" height={20}
      style={{ margin: "0 auto 5px", backgroundColor: "#ffffff5e" }} />
    <Skeleton variant="text" width="80%" height={24}
      style={{ margin: "0 auto 15px", backgroundColor: "#ffffff5e" }} />
    
    {/* Informação do time */}
    <Skeleton variant="text" width="60%" height={20}
      style={{ margin: "0 auto 15px", backgroundColor: "#ffffff5e" }} />
    
    {/* Estatísticas - layout 3x2 */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Skeleton variant="text" width="80%" height={14} style={{ backgroundColor: "#ffffff5e" }} />
          <Skeleton variant="text" width="50%" height={24} style={{ backgroundColor: "#ffffff5e" }} />
        </div>
      ))}
    </div>
  </PlayerCardSkeleton>
);


export default function Players() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const {
    data: players = [],
    isLoading,
    error,
  } = useSearchPlayers(searchQuery, {
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setSearchQuery(searchTerm);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const navigateToPlayerDetails = (playerId: string | number) => {
    navigate(`/dashboard/jogador/${playerId}`);
  };

  // Estatísticas que queremos exibir
  const statsToShow = [
    { key: "player_rating", label: "NOTA" },
    { key: "player_goals", label: "GOLS" },
    { key: "player_assists", label: "ASSIST." },
    { key: "player_passes_accuracy", label: "PRECISÃO" },
    { key: "player_shots_total", label: "CHUTES" },
    { key: "player_match_played", label: "JOGOS" },
  ];

  return (
    <Container>
      <Content>
        <Card>
          <SearchBar>
            <input
              type="text"
              placeholder="Digite o nome do jogador"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button onClick={handleSearch}>
              <FaSearch size={20} />
              Buscar
            </button>
          </SearchBar>

          {isLoading && (
            <SkeletonGrid>
              {[...Array(6)].map((_, i) => (
                <PlayerSkeleton key={i} />
              ))}
            </SkeletonGrid>
          )}

          {error && (
            <p className="error-message">
              Não foi possível buscar os jogadores. Tente novamente.
            </p>
          )}

          {!isLoading && players.length > 0 && (
            <PlayerGrid>
              {players.map((player) => (
                <PlayerCard 
                  key={player.player_key} 
                  onClick={() => navigateToPlayerDetails(player.player_key)}
                  style={{ cursor: 'pointer' }}
                >
                  <PlayerImage>
                    {player.player_image ? (
                      <img src={player.player_image} alt={player.player_name} />
                    ) : (
                      <div className="no-image">
                        {player.player_name?.substring(0, 1).toUpperCase()}
                      </div>
                    )}
                  </PlayerImage>
                  <PlayerInfo>
                    <span className="position">
                      {translatePosition(player.player_type)}
                    </span>
                    <h3>{player.player_name}</h3>

                    <TeamInfo>
                      {player.teamLogo && (
                        <img src={player.teamLogo} alt={player.team_name} />
                      )}
                      <span>{player.team_name || player.player_country}</span>
                    </TeamInfo>

                    <StatsInfo>
                      {statsToShow.map((stat) => (
                        <div key={stat.key} className="stat-item">
                          <span className="stat-name">{stat.label}</span>
                          <span className="stat-value">
                            {player[stat.key as keyof Player] || 0}
                          </span>
                        </div>
                      ))}
                    </StatsInfo>
                  </PlayerInfo>
                </PlayerCard>
              ))}
            </PlayerGrid>
          )}

          {!isLoading && players.length === 0 && searchQuery && (
            <p className="no-results">
              Nenhum jogador encontrado com esse nome.
            </p>
          )}
        </Card>
      </Content>
    </Container>
  );
}
